import { Warranty, WarrantyBrand, WarrantyClaimStatus, WarrantyPurchase } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useWarrantyClaimContext } from '@components';
import { t } from '@translations';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type WarrantyStepperContextProps = {
  children: React.ReactNode;
  partsDiscount?: number;
  laborPrice?: number;
  troubleshootingPrice?: number;
};

type WarrantyStepperContextType = {
  activeStep: number;
  completed: {
    [k: number]: boolean;
  };
  totalSteps: () => number;
  handleNext: () => void;
  handleBack: () => void;
  handleReset: () => void;
  steps: string[];
  hasDraft: boolean;
  handleSaveDraft: () => void;
  showAlert: boolean;
  handleCloseAlert: () => void;
  handleSetActiveStep: (step: number) => void;
};

export const WarrantyStepperContext = createContext<WarrantyStepperContextType | null>(null);

export const WarrantyStepperProvider = ({
  children,
  partsDiscount,
  laborPrice,
  troubleshootingPrice,
}: WarrantyStepperContextProps) => {
  const { dealerName, userEmail, form, setSerialNoValid, userFullName } = useWarrantyClaimContext();
  const [hasDraft, setHasDraft] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});

  const warrantyResetState: Warranty = useMemo(
    () => ({
      brand: WarrantyBrand.Honda,
      serialNo: '',
      purchase: WarrantyPurchase.Private,
      dealerName: dealerName,
      dealerEmail: userEmail || '',
      dealerContact: userFullName || '',
      status: WarrantyClaimStatus.New,
      partsDiscount: partsDiscount || 0,
      laborPrice: laborPrice || 0,
      troubleshootingPrice: troubleshootingPrice || 0,
    }),
    [dealerName, userEmail, userFullName, partsDiscount, laborPrice, troubleshootingPrice]
  );

  const steps = useMemo(() => {
    return [
      t(TextCodes.Warranty.Block.BrandSerialNumberSelection),
      t(TextCodes.Warranty.Block.WarrantyClaimForm),
    ];
  }, []);

  const totalSteps = useCallback(() => {
    return steps.length;
  }, [steps.length]);

  const handleNext = useCallback(() => {
    const newActiveStep = activeStep + 1;
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    setActiveStep(newActiveStep);
  }, [activeStep, completed]);

  const handleBack = useCallback(() => {
    const newCompleted = completed;
    newCompleted[activeStep] = false;
    setCompleted(newCompleted);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, [activeStep, completed]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setCompleted({});
    localStorage.removeItem('warrantyFormData');
    setShowAlert(false);
    setHasDraft(false);
    setSerialNoValid(null);
    form.reset(warrantyResetState);
    window.scrollTo(0, 0);
  }, [form, warrantyResetState, setSerialNoValid]);

  const handleSaveDraft = useCallback(() => {
    const formData = form.getValues();
    localStorage.setItem('warrantyFormData', JSON.stringify(formData));
    setHasDraft(true);
    setShowAlert(true);
  }, [form]);

  const handleCloseAlert = useCallback(() => {
    setShowAlert(false);
  }, []);

  const handleSetActiveStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  useEffect(() => {
    const savedFormData = localStorage.getItem('warrantyFormData');
    if (savedFormData) {
      const parsedData = JSON.parse(savedFormData);
      form.reset(parsedData);
      setShowAlert(true);
      setHasDraft(true);
    }
  }, [form]);

  const contextValue = useMemo(
    () => ({
      activeStep,
      completed,
      totalSteps,
      handleNext,
      handleBack,
      handleReset,
      steps,
      hasDraft,
      handleSaveDraft,
      showAlert,
      handleCloseAlert,
      handleSetActiveStep,
    }),
    [
      activeStep,
      completed,
      totalSteps,
      handleNext,
      handleBack,
      handleReset,
      steps,
      hasDraft,
      handleSaveDraft,
      showAlert,
      handleCloseAlert,
      handleSetActiveStep,
    ]
  );
  return (
    <WarrantyStepperContext.Provider value={contextValue}>
      {children}
    </WarrantyStepperContext.Provider>
  );
};

export const useWarrantyStepperContext = () => {
  const context = useContext(WarrantyStepperContext);
  if (!context) {
    throw new Error('useWarranty must be used within a WarrantyStepperContextProvider');
  }
  return context;
};
